<script lang="ts" setup>
import { Mailing } from '@/@types'
import { Item } from '@/@types/cart'
import { Order, OrdersnapShot } from '@/@types/order'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Dialog, DialogClose, DialogFooter, DialogHeader, DialogOverlay, DialogTitle, DialogTrigger } from '@/components/ui/dialog'
import DialogContent from '@/components/ui/dialog/DialogContent.vue'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { cn } from '@/lib/utils'
import { useForm, usePage } from '@inertiajs/vue3'
import { Loader2Icon, Ticket } from 'lucide-vue-next'
import { ref } from 'vue'
import ParticipantCompleteInfo from './ParticipantCompleteInfo.vue'
import ParticipantNotFound from './ParticipantNotFound.vue'
import TicketTitle from './TicketTitle.vue'

interface OrderShow extends Order {
    order_snap_shot: OrdersnapShot
}

withDefaults(
    defineProps<{
        mailing: Mailing
        orderItem: Item
        order: OrderShow
        hasCancelTicket?: boolean
        isAdmin?: boolean
    }>(),
    {},
)

const {user} = usePage().props

const emit = defineEmits<{
    (e: 'remove-ticket', mailingId: number): void
}>()

function removeTicket(mailingId: number) {
    emit('remove-ticket', mailingId)
}

const form = useForm({})

const typeToExclude = ref<string>('')
</script>

<template>

    <div class="flex w-full cursor-pointer flex-wrap items-center gap-4">
        <div class="flex flex-1 flex-wrap items-center justify-between gap-4">
            <div class="flex items-center gap-2 font-semibold">
                <div class="flex size-11 items-center justify-center rounded-full bg-cyan-50 text-cyan-700">
                    <Ticket />
                </div>
                <div class="flex flex-wrap items-center gap-x-2">
                    {{ $t(orderItem.ticket.category) }} <small v-if="mailing && mailing.code"> - {{ mailing.code }}</small>
                </div>
            </div>

            <div v-if="!mailing" class="w-full max-w-44">
                <div>
                    {{ $t('Group') }}
                </div>
                <div>
                    <strong>{{ $t(`groups.${order.group}`) }}</strong>
                </div>
            </div>
        </div>
 
        <div
            :class="cn('flex flex-1 flex-wrap items-center justify-end gap-2 text-sm md:gap-6')"
            v-if="order.status === 'paid' || order.status === 'released'"
        >
            <Badge variant="pending" v-if="!mailing">{{ $t('Pending') }}</Badge>
            <Dialog v-if="(mailing && mailing.status === 'ticket_generated')">
                <DialogTrigger as-child>
                    <Button size="sm" class="bg-cyan-50 text-cyan-600 hover:bg-cyan-50/90">
                        {{ $t('See more') }}
                    </Button>
                </DialogTrigger>
                <DialogContent class="w-full md:max-w-4xl">
                    <TicketTitle>{{ $t(orderItem.ticket.category) }}</TicketTitle>
                    <ParticipantCompleteInfo
                        v-if="mailing && mailing.status === 'ticket_generated'"
                        :mailing="mailing"
                        :order-item="orderItem"
                        @removeTicket="removeTicket"
                    />
                </DialogContent>
            </Dialog>
     
   
            <Dialog v-if="!mailing">
                <DialogTrigger as-child>
                    <Button size="sm" :disabled="!isAdmin && orderItem.ticket.workshop.disable_actions" class="bg-cyan-50 text-cyan-600 hover:bg-cyan-50/90">
                        {{ $t('Attach participant') }}
                    </Button>
                </DialogTrigger>
                <DialogContent class="w-full md:max-w-4xl">
                    <TicketTitle>{{ $t(orderItem.ticket.category) }}</TicketTitle>

                    <ParticipantNotFound
                        v-if="!mailing"
                        :order-item="orderItem"
                        :route="isAdmin ? `/admin/items/${orderItem.id}/mailings/new` : `/items/${orderItem.id}/mailings/new`"
                    />
                </DialogContent>
            </Dialog>
       
            <Dialog v-if="!mailing ">
                <DialogOverlay class="z-60 bg-black/10" />
                <DialogTrigger class="cursor-pointer" as-child>
                    <Button size="sm" :disabled="!isAdmin && orderItem.ticket.workshop.disable_actions" class="bg-red-50 text-red-600 hover:bg-red-50/90" v-if="hasCancelTicket">
                        {{ $t('Cancel ticket') }}
                    </Button>
                </DialogTrigger>
                <DialogContent class="font-inter p-8 text-gray-800">
                    <DialogHeader class="flex gap-2">
                        <DialogTitle class="text-2xl">{{ $t('Are you sure you want to cancel ticket', { order_id: order.id }) }}</DialogTitle>
                    </DialogHeader>

                    <div>
                        <Label required for="type">{{ $t("Please type 'CANCELAR' to confirm.") }}</Label>
                        <Input
                            v-model="typeToExclude"
                            type="text"
                            id="type"
                            @input="(e: Event) => (typeToExclude = (e.target as HTMLInputElement).value.toUpperCase())"
                        />
                    </div>

                    <DialogFooter class="pt-2">
                        <form
                            @submit.prevent="form.delete(`/admin/items/${orderItem.id}/process_invalidation`, { preserveState: false })"
                            class="flex w-full flex-col gap-6"
                        >
                            <div class="flex justify-between">
                                <DialogClose>
                                    <Button
                                        @click="typeToExclude = ''"
                                        type="button"
                                        variant="ghost"
                                        class="text-primary hover:text-primary hover:bg-primary/3 min-w-2/12"
                                    >
                                        {{ $t('Back') }}
                                    </Button>
                                </DialogClose>
                                <Button :disabled="form.processing || typeToExclude !== 'CANCELAR'" class="min-w-2/12">
                                    <template v-if="!form.processing">
                                        {{ $t('Cancel ticket') }}
                                    </template>
                                    <template v-if="form.processing">
                                        <span>
                                            <Loader2Icon class="animate-spin" />
                                        </span>
                                    </template>
                                </Button>
                            </div>
                        </form>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <Badge v-if="mailing" :variant="mailing?.status && mailing.status === 'canceled' ? 'revoked' : mailing.status">
                {{ $t(`mailing_status.${mailing.status}`) }}
            </Badge>
        </div>

        <Badge v-if="!mailing && (order.status === 'canceled' || order.status === 'canceled_by_admin')" variant="canceled">
            {{ $t(`mailing_status.canceled_by_order`) }}
        </Badge>
        <Badge v-else-if="mailing && (order.status === 'canceled' || order.status === 'canceled_by_admin')" variant="canceled">
            {{ $t(`mailing_status.canceled_by_order`) }}
        </Badge>
    </div>
</template>
